/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// frontend/src/components/CustomerBookingForm.js
import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

/**
 * Clean Google Maps formatted address by removing unwanted information
 * Removes: "UK", double town names, "Managed by..." text
 */
function cleanGoogleMapsAddress(address) {
  if (!address || typeof address !== 'string') {
    return address;
  }
  
  let cleaned = address.trim();
  
  // Remove "Managed by..." text and everything after it
  const managedByIndex = cleaned.indexOf('Managed by');
  if (managedByIndex !== -1) {
    cleaned = cleaned.substring(0, managedByIndex).trim();
  }
  
  // Remove ", UK" or "UK" at the end (case insensitive)
  cleaned = cleaned.replace(/,\s*UK\s*$/i, '').trim();
  cleaned = cleaned.replace(/\s+UK\s*$/i, '').trim();
  
  // Remove duplicate town names
  // Split by comma and process each part
  const parts = cleaned.split(',').map(p => p.trim()).filter(p => p.length > 0);
  const uniqueParts = [];
  const seen = new Set();
  
  // Common street suffixes to ignore when comparing
  const streetSuffixes = ['road', 'street', 'avenue', 'lane', 'drive', 'way', 'close', 'place', 
    'court', 'gardens', 'park', 'hill', 'terrace', 'row', 'crescent', 'grove', 'walk', 
    'square', 'yard', 'mews', 'end', 'corner', 'view', 'rise', 'vale', 'green', 'mead', 
    'bank', 'bridge', 'gate', 'side', 'wood', 'heath', 'ford', 'well'];
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    // Normalize for comparison (lowercase, remove common street suffixes)
    const normalized = part.toLowerCase()
      .replace(new RegExp(`\\s+(${streetSuffixes.join('|')})$`, 'i'), '')
      .trim();
    
    // Skip if we've seen this normalized part before (avoid duplicates)
    // But always keep the last part (usually postcode or main area) and postcodes (contain numbers/letters pattern)
    const isPostcode = /^[A-Z]{1,2}\d{1,2}\s?\d[A-Z]{2}$/i.test(part);
    if (!seen.has(normalized) || i === parts.length - 1 || isPostcode) {
      uniqueParts.push(part);
      if (!isPostcode) {
        seen.add(normalized);
      }
    }
  }
  
  cleaned = uniqueParts.join(', ').trim();
  
  // Final cleanup: remove trailing commas
  cleaned = cleaned.replace(/,\s*$/, '').trim();
  
  return cleaned;
}

const CustomerBookingForm = ({ show, onHide, onSubmit }) => {
  // Form state - exactly matching My Diary Add Job structure
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    from: '',
    to: '',
    dateTime: new Date(),
    passengers: 1,
    notes: '',
    returnTrip: false,
    dog: false,
    disabled: false,
    waitAndReturn: false,
    waitHours: '',
    fareAmount: ''
  });

  // Date picker modals (same as My Diary Add Job)
  const [showDateModal, setShowDateModal] = useState(false);
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [showYearModal, setShowYearModal] = useState(false);
  const [showHourModal, setShowHourModal] = useState(false);
  const [showMinuteModal, setShowMinuteModal] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [isContactAutoPopulated, setIsContactAutoPopulated] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Helper functions (same as My Diary Add Job)
  const toISOStringLocal = (date) => {
    const tzoffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzoffset).toISOString();
  };

  const createSafeDate = (dateString) => {
    try {
      return new Date(dateString);
    } catch (error) {
      console.warn('Error creating date:', error);
      return new Date();
    }
  };

  const calculateReturnTime = (dateTime, waitHours) => {
    if (!dateTime || !waitHours) return '';
    const returnTime = new Date(dateTime.getTime() + (waitHours * 60 * 60 * 1000));
    return returnTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Clear form
  const clearForm = () => {
    setFormData({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      from: '',
      to: '',
      dateTime: new Date(),
      passengers: 1,
      notes: '',
      returnTrip: false,
      dog: false,
      disabled: false,
      waitAndReturn: false,
      waitHours: '',
      fareAmount: ''
    });
    setIsContactAutoPopulated(false);
  };

  // Swap From and To fields
  const swapFromTo = () => {
    setFormData(prev => ({
      ...prev,
      from: prev.to,
      to: prev.from
    }));
  };

  // Search by exact phone or email match
  const searchByContact = async (phone, email) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (phone && phone.trim() !== '') {
        params.append('phone', phone.trim());
      }
      if (email && email.trim() !== '') {
        params.append('email', email.trim());
      }
      
      if (params.toString() === '') {
        return; // No search criteria
      }
      
      const { data } = await axios.get(`/diary/search-by-contact?${params.toString()}`);
      
      if (data.found && data.customer) {
        // Auto-populate all fields from the found customer
        setFormData(prev => ({
          ...prev,
          customerName: data.customer.customerName || prev.customerName,
          customerEmail: data.customer.customerEmail || prev.customerEmail,
          customerPhone: data.customer.customerPhone || prev.customerPhone,
          from: data.customer.from || prev.from,
          to: data.customer.to || prev.to
        }));
        setIsContactAutoPopulated(true);
        toast.success(`Customer found: ${data.customer.customerName}`, {
          position: 'top-center',
          autoClose: 2000
        });
      } else {
        setIsContactAutoPopulated(false);
      }
    } catch (err) {
      console.error('Error searching by contact:', err);
      setIsContactAutoPopulated(false);
    }
  };

  // Form change handler
  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    
    // Search by phone when entered
    if (name === 'customerPhone') {
      setIsContactAutoPopulated(false);
      
      // Debounced search by phone
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (value && value.trim().length >= 5) { // Search when at least 5 digits entered
        searchTimeoutRef.current = setTimeout(() => {
          searchByContact(value, null);
        }, 800); // Wait 800ms after user stops typing
      }
    }
    
    // Search by email when entered
    if (name === 'customerEmail') {
      setIsContactAutoPopulated(false);
      
      // Debounced search by email
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Check if email looks valid before searching
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && emailPattern.test(value)) {
        searchTimeoutRef.current = setTimeout(() => {
          searchByContact(null, value);
        }, 800); // Wait 800ms after user stops typing
      }
    }
    
    // Clear auto-populated flag if user manually changes other fields
    if (name === 'customerName' || name === 'from' || name === 'to') {
      if (isContactAutoPopulated) {
        setIsContactAutoPopulated(false);
      }
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerPhone || formData.customerPhone.trim() === '') {
      toast.error('Enter a valid phone number.');
      return;
    }
    
    const iso = toISOStringLocal(formData.dateTime);
    try {
      if (createSafeDate(iso) < createSafeDate(new Date())) {
        toast.error('Date must be in the future.');
        return;
      }
    } catch (error) {
      console.warn('Error validating date:', error);
      toast.error('Invalid date format.');
      return;
    }

    try {
      setSaving(true);
      
      // Clean addresses before submission
      const cleanedFrom = cleanGoogleMapsAddress(formData.from);
      const cleanedTo = cleanGoogleMapsAddress(formData.to);
      
      // Call the onSubmit prop with form data
      await onSubmit({
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        from: cleanedFrom,
        to: cleanedTo,
        dateTime: iso,
        passengers: formData.passengers,
        returnTrip: formData.returnTrip,
        dog: formData.dog,
        disabled: formData.disabled,
        waitAndReturn: formData.waitAndReturn,
        waitHours: formData.waitHours,
        notes: formData.notes,
        fareAmount: formData.fareAmount,
        status: 'pending'
      });

      toast.success('Booking request submitted successfully');
      
      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        from: '',
        to: '',
        dateTime: new Date(),
        passengers: 1,
        notes: '',
        returnTrip: false,
        dog: false,
        disabled: false,
        waitAndReturn: false,
        waitHours: '',
        fareAmount: ''
      });
      
      onHide();
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => {clearForm(); onHide();}} size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="sticky-top bg-white border-bottom">
            <Modal.Title>Book Your Taxi</Modal.Title>
            <Button type="submit" variant="success" disabled={saving} className="ms-auto">
              {saving ? 'Submitting…' : 'Submit Booking'}
            </Button>
          </Modal.Header>
          <Modal.Body className="overflow-auto" style={{ maxHeight: '70vh' }}>
            {/* Phone Number Section - First Field */}
            <div className="border-bottom pb-2 mb-2">
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleFormChange}
                  placeholder="Enter phone number"
                  required
                  className={isContactAutoPopulated ? 'border-success' : ''}
                />
              </Form.Group>
            </div>

            {/* Date & Time Section - Exactly matching My Diary Add Job */}
            <div className="border-bottom pb-2 mb-2">
              <h6 className="text-muted mb-2">📅 Journey Date</h6>
              <Row className="g-2">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Month</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][formData.dateTime.getMonth()] : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][new Date().getMonth()]}
                    onClick={() => setShowMonthModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select month"
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? formData.dateTime.getDate() : new Date().getDate()}
                    onClick={() => setShowDateModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select date"
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Year</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? formData.dateTime.getFullYear() : new Date().getFullYear()}
                    onClick={() => setShowYearModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select year"
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
              </Row>
              <Row className="g-2 mt-0">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Hour</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? (formData.dateTime.getHours() % 12 || 12) : (new Date().getHours() % 12 || 12)}
                    onClick={() => setShowHourModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select hour"
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Minute</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? Math.floor(formData.dateTime.getMinutes() / 5) * 5 : Math.floor(new Date().getMinutes() / 5) * 5}
                    onClick={() => setShowMinuteModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select minute"
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">AM/PM</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dateTime && formData.dateTime instanceof Date ? (formData.dateTime.getHours() < 12 ? 'AM' : 'PM') : (new Date().getHours() < 12 ? 'AM' : 'PM')}
                    onClick={() => {
                      const newDate = new Date(formData.dateTime || new Date());
                      const currentHour = newDate.getHours();
                      if (currentHour >= 12) {
                        newDate.setHours(currentHour - 12);
                      } else {
                        newDate.setHours(currentHour + 12);
                      }
                      setFormData(prev => ({ ...prev, dateTime: newDate }));
                    }}
                    readOnly
                    size="sm"
                    placeholder="Click to toggle AM/PM"
                    style={{ 
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}
                  />
                </Col>
              </Row>
            </div>

            {/* Wait and Return Section - Exactly matching My Diary Add Job */}
            <div className="border-bottom pb-2 mb-2">
              <Row className="g-2">
                <Col xs={12}>
                  <Form.Check
                    type="checkbox"
                    label="Wait & Return"
                    name="waitAndReturn"
                    checked={formData.waitAndReturn || false}
                    onChange={handleFormChange}
                    className="mb-2"
                  />
                </Col>
                {formData.waitAndReturn && (
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Expected Return Time (hours)</Form.Label>
                      <Form.Control
                        type="number"
                        name="waitHours"
                        min="0.5"
                        max="24"
                        step="0.5"
                        value={formData.waitHours || ''}
                        onChange={handleFormChange}
                        placeholder="4.5"
                        size="sm"
                      />
                      <Form.Text className="text-muted small">
                        {formData.waitHours && formData.dateTime && formData.dateTime instanceof Date ? 
                          `Return time: ${calculateReturnTime(formData.dateTime, formData.waitHours)}` : 
                          'Enter hours to wait before return'
                        }
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </div>

            {/* Customer Information Section - Exactly matching My Diary Add Job */}
            <div className="border-bottom pb-2 mb-2">
              <h6 className="text-muted mb-2">👤 Customer Information</h6>
              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Name</Form.Label>
                    <Form.Control
                      name="customerName" type="text"
                      value={formData.customerName}
                      onChange={handleFormChange}
                      placeholder="Enter your name"
                      required
                      size="sm"
                      className={isContactAutoPopulated ? 'border-success' : ''}
                    />
                  </Form.Group>
                  {isContactAutoPopulated && (
                    <small className="text-success">Customer details auto-populated</small>
                  )}
                </Col>
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small mb-1">Email</Form.Label>
                      <Form.Control
                        name="customerEmail" type="email"
                        value={formData.customerEmail}
                        onChange={handleFormChange}
                        placeholder="Enter your email"
                        required
                        size="sm"
                        className={isContactAutoPopulated ? 'border-success' : ''}
                      />
                    </Form.Group>
                  </Col>
              </Row>
            </div>

            {/* Journey Information Section - Exactly matching My Diary Add Job */}
            <div className="border-bottom pb-2 mb-2">
              <h6 className="text-muted mb-2">🚗 Journey Details</h6>
              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">From</Form.Label>
                    <Form.Control
                      name="from" type="text"
                      value={formData.from}
                      onChange={handleFormChange}
                      placeholder="Pickup location"
                      required
                      size="sm"
                      className={isContactAutoPopulated ? 'border-success' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-2">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <Form.Label className="small mb-0">To</Form.Label>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={swapFromTo}
                        title="Swap From and To"
                        style={{ 
                          width: '40px', 
                          height: '32px', 
                          padding: '0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: '0.7',
                          fontSize: '22px',
                          fontWeight: 'bold'
                        }}
                      >
                        <div>⌃</div>
                        <div>⌄</div>
                      </Button>
                    </div>
                    <Form.Control
                      name="to" type="text"
                      value={formData.to}
                      onChange={handleFormChange}
                      placeholder="Destination"
                      required
                      size="sm"
                      className={isContactAutoPopulated ? 'border-success' : ''}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2">
                <Col xs={6} sm={3}>
                  <Form.Group className="mb-1">
                    <Form.Label className="small">Passengers</Form.Label>
                    <Form.Control
                      name="passengers" type="number" min={1}
                      value={formData.passengers||''}
                      onChange={handleFormChange}
                      required
                      size="sm"
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox" label="🐕 Dog/Pet"
                    name="dog"
                    checked={formData.dog || false}
                    onChange={handleFormChange}
                    className="mb-1"
                  />
                </Col>
                <Col xs={6} sm={3}>
                  <Form.Group className="mb-1">
                    <Form.Label className="small">Fare (£)</Form.Label>
                    <Form.Control
                      name="fareAmount"
                      type="text"
                      placeholder="e.g. £12.50"
                      value={formData.fareAmount || ''}
                      onChange={handleFormChange}
                      size="sm"
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox" label="♿ Disabled"
                    name="disabled"
                    checked={formData.disabled || false}
                    onChange={handleFormChange}
                    className="mb-1"
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Check
                    type="checkbox" label="🔄 Return Trip"
                    name="returnTrip"
                    checked={formData.returnTrip || false}
                    onChange={handleFormChange}
                    className="mb-1"
                  />
                </Col>
              </Row>
            </div>

            {/* Notes Section - Exactly matching My Diary Add Job */}
            <div>
              <h6 className="text-muted mb-2">📝 Notes</h6>
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea"
                  rows="2"
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleFormChange}
                  maxLength={100}
                  placeholder="Any additional notes..."
                  size="sm"
                />
                <Form.Text className="text-muted small">
                  {formData.notes ? formData.notes.length : 0}/100 characters
                </Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {clearForm(); onHide();}}>Cancel</Button>
            <Button type="submit" variant="success" disabled={saving} className="ms-auto">
              {saving ? 'Submitting…' : 'Submit Booking'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Date Picker Modals - Exactly matching My Diary Add Job */}
      {/* Date Picker Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">
            {(() => {
              const currentDate = formData.dateTime || new Date();
              return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][currentDate.getMonth()]} ${currentDate.getFullYear()}`;
            })()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="calendar-grid">
            {/* Day headers */}
            <div className="calendar-header">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="calendar-day-header">{day}</div>
              ))}
            </div>
            
            {/* Calendar days */}
            <div className="calendar-body">
              {(() => {
                const currentDate = formData.dateTime || new Date();
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                const startDate = new Date(firstDay);
                startDate.setDate(startDate.getDate() - firstDay.getDay());
                
                const days = [];
                for (let i = 0; i < 42; i++) {
                  const date = new Date(startDate);
                  date.setDate(startDate.getDate() + i);
                  const isCurrentMonth = date.getMonth() === month;
                  const isSelected = date.getDate() === currentDate.getDate() && 
                                   date.getMonth() === currentDate.getMonth() && 
                                   date.getFullYear() === currentDate.getFullYear();
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  days.push(
                    <div
                      key={i}
                      className={`calendar-day ${isCurrentMonth ? '' : 'text-muted'} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                      onClick={() => {
                        if (isCurrentMonth) {
                          const newDate = new Date(formData.dateTime || new Date());
                          newDate.setDate(date.getDate());
                          setFormData(prev => ({ ...prev, dateTime: newDate }));
                          setShowDateModal(false);
                        }
                      }}
                    >
                      {date.getDate()}
                    </div>
                  );
                }
                return days;
              })()}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowDateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Month Picker Modal */}
      <Modal show={showMonthModal} onHide={() => setShowMonthModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Month</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
              <Button
                key={index}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(formData.dateTime || new Date());
                  newDate.setMonth(index);
                  setFormData(prev => ({ ...prev, dateTime: newDate }));
                  setShowMonthModal(false);
                }}
                className={`${formData.dateTime && formData.dateTime.getMonth() === index ? 'active' : ''}`}
              >
                {month}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowMonthModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Year Picker Modal */}
      <Modal show={showYearModal} onHide={() => setShowYearModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Year</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {[2025, 2026, 2027].map((year) => (
              <Button
                key={year}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(formData.dateTime || new Date());
                  newDate.setFullYear(year);
                  setFormData(prev => ({ ...prev, dateTime: newDate }));
                  setShowYearModal(false);
                }}
                className={`${formData.dateTime && formData.dateTime.getFullYear() === year ? 'active' : ''}`}
              >
                {year}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowYearModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hour Picker Modal */}
      <Modal show={showHourModal} onHide={() => setShowHourModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Time Required</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex">
            <div className="flex-grow-1">
              {/* Row 1: Hours 1-6 */}
              <div className="row mb-2 g-1">
                {[1, 2, 3, 4, 5, 6].map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const newDate = new Date(formData.dateTime || new Date());
                        const currentHour = newDate.getHours();
                        const isPM = currentHour >= 12;
                        const newHour = isPM ? hour + 12 : hour;
                        if (hour === 12) {
                          newDate.setHours(isPM ? 12 : 0);
                        } else {
                          newDate.setHours(newHour);
                        }
                        setFormData(prev => ({ ...prev, dateTime: newDate }));
                        setShowHourModal(false);
                      }}
                      className={`${formData.dateTime && (formData.dateTime.getHours() % 12 || 12) === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {hour}
                    </Button>
                  </div>
                ))}
              </div>
              {/* Row 2: Hours 7-12 */}
              <div className="row g-1">
                {[7, 8, 9, 10, 11, 12].map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const newDate = new Date(formData.dateTime || new Date());
                        const currentHour = newDate.getHours();
                        const isPM = currentHour >= 12;
                        const newHour = isPM ? hour + 12 : hour;
                        if (hour === 12) {
                          newDate.setHours(isPM ? 12 : 0);
                        } else {
                          newDate.setHours(newHour);
                        }
                        setFormData(prev => ({ ...prev, dateTime: newDate }));
                        setShowHourModal(false);
                      }}
                      className={`${formData.dateTime && (formData.dateTime.getHours() % 12 || 12) === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {hour}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="ms-2 d-flex flex-column gap-1">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(formData.dateTime || new Date());
                  const currentHour = newDate.getHours();
                  if (currentHour >= 12) {
                    newDate.setHours(currentHour - 12);
                  }
                  setFormData(prev => ({ ...prev, dateTime: newDate }));
                }}
                className={`${formData.dateTime && formData.dateTime.getHours() < 12 ? 'active' : ''}`}
                style={{ minWidth: '45px', fontSize: '0.8rem' }}
              >
                am
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(formData.dateTime || new Date());
                  const currentHour = newDate.getHours();
                  if (currentHour < 12) {
                    newDate.setHours(currentHour + 12);
                  }
                  setFormData(prev => ({ ...prev, dateTime: newDate }));
                }}
                className={`${formData.dateTime && formData.dateTime.getHours() >= 12 ? 'active' : ''}`}
                style={{ minWidth: '45px', fontSize: '0.8rem' }}
              >
                pm
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowHourModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowHourModal(false)}>
            SET
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Minute Picker Modal */}
      <Modal show={showMinuteModal} onHide={() => setShowMinuteModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Minute</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          {/* Row 1: Minutes 00, 05, 10, 15, 20, 25 */}
          <div className="row mb-2 g-1">
            {[0, 5, 10, 15, 20, 25].map((minute) => (
              <div key={minute} className="col-2">
                <Button
                  variant={minute === 0 || minute === 15 ? "outline-success" : "outline-primary"}
                  size="sm"
                  onClick={() => {
                    const newDate = new Date(formData.dateTime || new Date());
                    newDate.setMinutes(minute);
                    setFormData(prev => ({ ...prev, dateTime: newDate }));
                    setShowMinuteModal(false);
                  }}
                  className={`${formData.dateTime && Math.floor(formData.dateTime.getMinutes() / 5) * 5 === minute ? 'active' : ''} w-100`}
                  style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                >
                  {minute.toString().padStart(2, '0')}
                </Button>
              </div>
            ))}
          </div>
          {/* Row 2: Minutes 30, 35, 40, 45, 50, 55 */}
          <div className="row g-1">
            {[30, 35, 40, 45, 50, 55].map((minute) => (
              <div key={minute} className="col-2">
                <Button
                  variant={minute === 30 || minute === 45 ? "outline-success" : "outline-primary"}
                  size="sm"
                  onClick={() => {
                    const newDate = new Date(formData.dateTime || new Date());
                    newDate.setMinutes(minute);
                    setFormData(prev => ({ ...prev, dateTime: newDate }));
                    setShowMinuteModal(false);
                  }}
                  className={`${formData.dateTime && Math.floor(formData.dateTime.getMinutes() / 5) * 5 === minute ? 'active' : ''} w-100`}
                  style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                >
                  {minute.toString().padStart(2, '0')}
                </Button>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowMinuteModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowMinuteModal(false)}>
            SET
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
      
      {/* Calendar CSS Styles - Exactly matching My Diary Add Job */}
      <style>{`
        .calendar-grid {
          width: 100%;
        }
        
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 8px;
        }
        
        .calendar-day-header {
          text-align: center;
          font-weight: bold;
          font-size: 0.7rem;
          color: #6c757d;
          padding: 2px;
        }
        
        .calendar-body {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        
        .calendar-day {
          text-align: center;
          padding: 4px 2px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 0.75rem;
          transition: background-color 0.2s;
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .calendar-day:hover {
          background-color: #e9ecef;
        }
        
        .calendar-day.selected {
          background-color: #0d6efd;
          color: white;
        }
        
        .calendar-day.today {
          background-color: #ffc107;
          color: #212529;
          font-weight: bold;
        }
        
        .calendar-day.selected.today {
          background-color: #0d6efd;
          color: white;
        }
      `}</style>
    </>
  );
};

export default CustomerBookingForm;
